import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articles, type Lang } from "@/mocks/articles";
import { MAIN_SITE_URL } from "@/mocks/categories";
import JsonLd from "@/components/base/JsonLd";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

function resolveLang(lang: string): Lang {
  return lang === "pl" || lang === "es" || lang === "fr" ? lang : "en";
}

const SITE_URL = import.meta.env.VITE_SITE_URL;
const LOGO_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = resolveLang(i18n.language);
  const [open, setOpen] = useState<number | null>(0);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto max-w-3xl px-4 pb-20 pt-40 text-center md:px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">{t("article.notFound")}</h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            {t("article.back")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title.en,
    description: article.seoDesc.en,
    image: [article.image],
    author: { "@type": "Organization", name: "USFansSheet" },
    publisher: {
      "@type": "Organization",
      name: "USFansSheet",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: `${SITE_URL}/article/${article.slug}`,
    datePublished: "2026-08-20",
    dateModified: "2026-08-20",
    inLanguage: "en",
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Header />

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t("nav.home")}
          </Link>
          <span>/</span>
          <span className="text-foreground-800">{t("articles.title")}</span>
          <span>/</span>
          <span className="text-foreground-800">{article.category[lang]}</span>
        </nav>

        <span className="mt-6 inline-flex w-fit items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
          {article.category[lang]}
        </span>

        <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
          {article.title[lang]}
        </h1>

        {/* Featured image */}
        <div className="mt-7 aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <img
            src={article.image}
            alt={article.title[lang]}
            title={`${t("site.name")} ${article.title[lang]}`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        {/* Featured snippet */}
        <div className="mt-7 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
          <p className="text-base font-medium leading-relaxed text-foreground-800">{article.short[lang]}</p>
        </div>

        {/* Meta */}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {t("article.meta.author")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {t("article.meta.date")}
          </span>
        </div>

        <p className="mt-6 text-base leading-relaxed text-foreground-700">{article.intro[lang]}</p>

        {/* Sections */}
        {article.sections.map((section, idx) => (
          <section key={idx} className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">{section.heading[lang]}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{section.body[lang]}</p>
          </section>
        ))}

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("faq.title")}</h2>
          <div className="mt-6 space-y-3">
            {article.faq.map((item, idx) => {
              const isOpen = open === idx;
              return (
                <div key={idx} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : idx)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-foreground-900">{item.q[lang]}</span>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className={`ri-add-line text-sm transition-transform ${isOpen ? "rotate-45" : ""}`}></i>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-700">
                      {item.a[lang]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("articles.related")}</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/article/${r.slug}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="text-xs font-semibold text-primary-700">{r.category[lang]}</span>
                  <h3 className="mt-2 text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">
                    {r.title[lang]}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back + CTA */}
        <div className="mt-12 rounded-lg bg-primary-50 p-8 text-center">
          <p className="text-base leading-relaxed text-foreground-700">{t("article.related")}</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 sm:w-auto"
            >
              <i className="ri-arrow-left-line"></i>
              {t("article.back")}
            </Link>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              {t("article.cta")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}